import { Button, Label } from 'reactstrap';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipButton from './TooltipButton';
import ISelectFeatures from '../interfaces/ISelectFeatures';
import IFeatureOptions from '../interfaces/IFeatureOptions';
import { CampaignContext } from '../../../context/CampaignWrapper';

const SelectFeatures = ({
  variantIndex, setVariantIndex, variants, hide = false,
} : ISelectFeatures) => {
  const [translate] = useTranslation();
  const { campaign: { interfaceConfig: { sortFeatures }, textConfig: { variantUnavailable } } } = useContext(CampaignContext);
  const [features, setFeatures] = useState((variants[variantIndex] || {}).features || []);
  const { groupId } = variants[variantIndex] || {};

  const labelStyle = {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '19px',
  };

  const labels: {
    [key: string]: string
  } = {};

  const featureOptions = variants.reduce<IFeatureOptions>((obj, article) => {
    (article.features || []).forEach((f) => {
      // eslint-disable-next-line no-param-reassign
      if (!obj[f.key]) obj[f.key] = new Set();
      obj[f.key].add(f.value);
      if (f.label) labels[f.value] = f.label;
    });
    return obj;
  }, {});

  // Find variant with all features the same, except the one provided
  const findVariantIndex = (key: string, value: string) => variants.findIndex((variant) => (variant.features || [])
    .every((feature) => {
      if (feature?.key === key) return feature?.value === value;
      return features.find((f) => f?.key === feature?.key && f?.value === feature?.value);
    }));

  const updateFeatures = (key: string, value: string) => {
    setVariantIndex(findVariantIndex(key, value));
    setFeatures((feats) => (feats || []).map((feat) => (feat?.key === key ? { ...feat, key, value } : feat)));
  };

  const isWhite = (v: string) => ['#fff', '#ffffff', 'white'].includes(v.toLowerCase());

  return (
    <div className={`${hide ? 'd-none' : ''} mb-3`}>
      {(features || []).map(({ key, type }) => {
        const options = Array.from(featureOptions[key]);
        if (sortFeatures) options.sort();
        if (type === 'colour') {
          return (
            <div className="mb-1" key={`${groupId}-${key}`}>
              <Label style={labelStyle}>{translate('COMMON.COLOUR')}</Label>
              <div className="d-flex align-items-center flex-wrap">
                {options.map((v) => {
                  const isSelected = features.some((f) => key === f?.key && v === f?.value);
                  return (
                    <TooltipButton
                      tooltip={labels[v]}
                      className={`features-colours ${isSelected ? 'selected' : ''} ${isWhite(v) ? 'bright' : ''}`}
                      style={{ backgroundColor: v }}
                      onClick={() => updateFeatures(key, v)}
                      key={`${groupId}-${key}-${v}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        }

        if (type === 'buttons') {
          return (
            <div className="mb-1" key={`${groupId}-${key}`}>
              <Label style={labelStyle}>{key}</Label>
              <div className="d-flex align-items-center flex-wrap">
                {options.map((v) => {
                  const isSelected = features.some((f) => key === f?.key && v === f?.value);
                  return (
                    <Button
                      className={`features-buttons ${isSelected ? 'selected' : ''}`}
                      onClick={() => updateFeatures(key, v)}
                      key={`${groupId}-${key}-${v}`}
                    >
                      {v}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        }

        return (
          <div className="mb-2" key={`${groupId}-${key}`}>
            <Label className="w-100" style={labelStyle}>{key}</Label>
            <select
              className="features-select"
              onChange={(e) => {
                updateFeatures(key, e.target.value);
              }}
              value={(features.find((f) => f?.key === key) || {}).value}
            >
              {options.map((v) => {
                const isSelected = features.some((f) => key === f?.key && v === f?.value);
                return (
                  <option
                    value={v}
                    style={{ ...(isSelected && { border: `5px solid ${v}` }) }}
                    key={`${groupId}-${key}-${v}`}
                  >
                    {labels[v] || v}
                  </option>
                );
              })}
            </select>
          </div>
        );
      })}
      {variantIndex < 0 && <p>{variantUnavailable || translate('COMPONENTS.ARTICLE_CARD.VARIANT_UNAVAILABLE')}</p>}
    </div>
  );
};

export default SelectFeatures;
